import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Database,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react'
import { Button, Card } from '@/components/ui'
import {
  loadContractRunbookSnapshot,
  loadContractCoverageGapSummary,
  type ContractRunbookSnapshot,
  type ContractCoverageGapSummary,
} from '@/services/contractRunbookSafetyService'

// Бэкап-схема, снятая ДО прогона ранбуков (см. SERIES_CONTRACT_MERGE_AND_RUNBOOK_PLAN.md).
// Поле редактируемое — на случай следующего батча под другой датой.
const DEFAULT_BACKUP_SCHEMA = 'backup_20260703_series_contract'

// kind определяет семантику дельты:
//  conserved — финансовая строка, обязана НЕ меняться через ранбук (Δ≠0 = потеря);
//  money     — то же, но денежная сумma (₽);
//  contracts — договоры-контейнеры, ПАДАЮТ на число схлопнутых (падение ожидаемо);
//  invariant — нарушение, цель — 0 (падение до 0 = успех);
//  grows     — событий становится больше по мере разбора (рост ожидаем).
type Kind = 'conserved' | 'money' | 'contracts' | 'invariant' | 'grows'

type Metric = {
  key: keyof ContractRunbookSnapshot
  label: string
  kind: Kind
  hint?: string
}

type Group = { title: string; note: string; metrics: Metric[] }

const GROUPS: Group[] = [
  {
    title: '🔒 Финансовые строки — обязаны сохраниться',
    note: 'merge перепривязывает их на мастер ДО удаления дубля. Любая ненулевая Δ здесь = сработал ON DELETE CASCADE, строки потеряны.',
    metrics: [
      { key: 'royalty_charges_count', label: 'Начисления (charges), шт', kind: 'conserved' },
      { key: 'royalty_charges_amount_rub', label: 'Сумма начислений, ₽', kind: 'money' },
      { key: 'royalty_charges_confirmed', label: 'Подтверждённых начислений, шт', kind: 'conserved' },
      { key: 'royalty_payments_count', label: 'Платежи, шт', kind: 'conserved' },
      { key: 'royalty_payments_amount_rub', label: 'Сумма платежей, ₽', kind: 'money' },
      { key: 'royalty_allocations_count', label: 'Разноски (allocations), шт', kind: 'conserved' },
      { key: 'royalty_allocations_amount_rub', label: 'Сумма разносок, ₽', kind: 'money' },
      { key: 'royalty_series_terms_count', label: 'Условия серий (terms), шт', kind: 'conserved' },
      { key: 'studio_documents_count', label: 'Документы студий, шт', kind: 'conserved' },
      { key: 'studio_checklist_count', label: 'Чек-листы документов, шт', kind: 'conserved' },
      { key: 'staging_matched_count', label: 'Строки выписки с привязкой к договору, шт', kind: 'conserved' },
      {
        key: 'charges_orphaned',
        label: 'Осиротевшие начисления (contract_id → нет договора)',
        kind: 'conserved',
        hint: 'В норме всегда 0. Ненулевое — аномалия ссылочной целостности.',
      },
      {
        key: 'payments_orphaned',
        label: 'Осиротевшие платежи (contract_id → нет договора)',
        kind: 'conserved',
        hint: 'В норме всегда 0.',
      },
    ],
  },
  {
    title: '📉 Договоры-контейнеры — уменьшаются на число схлопнутых',
    note: 'Схлопывание дубля/инверсии удаляет один ряд contracts. Падение здесь ожидаемо и равно числу прогнанных пар.',
    metrics: [
      { key: 'contracts_total', label: 'Всего договоров', kind: 'contracts' },
      { key: 'contracts_main', label: 'MAIN-договоров', kind: 'contracts' },
    ],
  },
  {
    title: '🎯 Инварианты — цель 0 после ранбуков',
    note: 'R1 обнуляет дубли MAIN, R2 — инверсии. Каждое схлопывание добавляет ENTITY_CHANGE-событие.',
    metrics: [
      { key: 'duplicate_main_pairs', label: 'Пары дублей MAIN (номер+дата)', kind: 'invariant' },
      {
        key: 'duplicate_main_pairs_entity_change',
        label: '— из них со сменой юрлица (R1)',
        kind: 'invariant',
      },
      { key: 'inverted_contracts', label: 'Инвертированные договоры (R2)', kind: 'invariant' },
      { key: 'contract_amendments_total', label: 'Событий (contract_amendments)', kind: 'grows' },
      { key: 'entity_change_events', label: 'Событий ENTITY_CHANGE', kind: 'grows' },
    ],
  },
]

const round2 = (n: number) => Math.round(n * 100) / 100

const intFmt = new Intl.NumberFormat('ru-RU')
const moneyFmt = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function fmt(kind: Kind, v: number | null | undefined): string {
  if (v == null) return '—'
  return kind === 'money' ? moneyFmt.format(v) : intFmt.format(v)
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('ru-RU')
  } catch {
    return iso
  }
}

// Является ли дельта тревожной (потеря финансовых данных).
function isConservedBreach(kind: Kind, before: number, now: number): boolean {
  return (kind === 'conserved' || kind === 'money') && round2(now - before) !== 0
}

export function ContractRunbookSafetyPage() {
  const navigate = useNavigate()
  const [now, setNow] = useState<ContractRunbookSnapshot | null>(null)
  const [baseline, setBaseline] = useState<ContractRunbookSnapshot | null>(null)
  const [coverage, setCoverage] = useState<ContractCoverageGapSummary | null>(null)
  const [backupSchema, setBackupSchema] = useState<string>(DEFAULT_BACKUP_SCHEMA)
  const [loading, setLoading] = useState(false)
  const [coverageLoading, setCoverageLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // «ДО» — всегда из бэкап-схемы (неизменяемый до-ранбучный снимок). Ручной
  // live-снимок намеренно не поддерживаем: зафиксировать его в середине разбора
  // = перезаписать корректный baseline частично отработанными данными.
  const loadBackupBaseline = useCallback(async (schema: string) => {
    const s = schema.trim()
    if (!s) {
      setBaseline(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const snap = await loadContractRunbookSnapshot(s)
      setBaseline(snap)
    } catch (e: any) {
      setBaseline(null)
      setError(e?.message ?? `Не удалось загрузить бэкап-схему ${s}`)
    } finally {
      setLoading(false)
    }
  }, [])

  // При старте: сразу тянем «ДО» из известной бэкап-схемы.
  useEffect(() => {
    void loadBackupBaseline(DEFAULT_BACKUP_SCHEMA)
  }, [loadBackupBaseline])

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const snap = await loadContractRunbookSnapshot()
      setNow(snap)
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось загрузить снапшот')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const runCoverage = useCallback(async () => {
    setCoverageLoading(true)
    setError(null)
    try {
      const cov = await loadContractCoverageGapSummary()
      setCoverage(cov)
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось посчитать покрытие')
    } finally {
      setCoverageLoading(false)
    }
  }, [])

  const breaches = useMemo(() => {
    if (!now || !baseline) return [] as Metric[]
    const flat = GROUPS.flatMap(g => g.metrics)
    return flat.filter(m => isConservedBreach(m.kind, baseline[m.key] as number, now[m.key] as number))
  }, [now, baseline])

  const orphanNow = now ? (now.charges_orphaned as number) + (now.payments_orphaned as number) : 0
  const hasAlarm = breaches.length > 0 || orphanNow > 0

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
          <ArrowLeft size={18} />
        </button>
        <ShieldAlert size={20} className="text-primary" />
        <h1 className="text-lg font-semibold">Безопасность ранбуков договоров</h1>
      </div>

      <p className="text-xs text-muted-foreground">
        Зафиксируйте «ДО» перед прогоном ранбука в SQL-редакторе, затем обновите снапшот после{' '}
        <code>COMMIT</code>. Финансовые строки должны сохраниться (Δ = 0), инварианты — упасть к 0.
      </p>

      {/* Панель действий */}
      <Card>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => void refresh()} disabled={loading}>
            <span className="inline-flex items-center gap-1.5">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Обновить «Сейчас»
            </span>
          </Button>

          {/* «ДО» из бэкап-схемы — единственный источник baseline */}
          <label className="inline-flex items-center gap-1.5 text-xs">
            <Database size={14} className="text-muted-foreground" />
            <span className="text-muted-foreground">ДО (бэкап-схема):</span>
            <input
              className="rounded-md border border-border bg-background px-2 py-1.5 text-xs font-mono w-64"
              value={backupSchema}
              onChange={e => setBackupSchema(e.target.value)}
              onBlur={() => void loadBackupBaseline(backupSchema)}
              onKeyDown={e => {
                if (e.key === 'Enter') void loadBackupBaseline(backupSchema)
              }}
              spellCheck={false}
            />
          </label>

          <div className="ml-auto text-xs text-muted-foreground">
            <div>ДО: {baseline?.schema ? `бэкап ${baseline.schema}` : '—'}</div>
            <div>Сейчас: {fmtTime(now?.captured_at)}</div>
          </div>
        </div>
      </Card>

      {error && (
        <Card className="border-red-500/40 bg-red-500/5">
          <div className="text-sm text-red-600">{error}</div>
        </Card>
      )}

      {/* Итоговый вердикт по сохранности */}
      {baseline && now && (
        <Card
          className={
            hasAlarm
              ? 'border-red-500/50 bg-red-500/10'
              : 'border-emerald-500/50 bg-emerald-500/10'
          }
        >
          <div className="flex items-start gap-2">
            {hasAlarm ? (
              <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
            ) : (
              <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
            )}
            <div className="text-sm">
              {hasAlarm ? (
                <>
                  <div className="font-semibold text-red-700">
                    Обнаружена потеря финансовых строк
                  </div>
                  <div className="text-red-700/80 mt-0.5">
                    Изменились сохраняемые величины: {breaches.map(b => b.label).join('; ')}
                    {orphanNow > 0 && '; появились осиротевшие ссылки'}. Проверьте последний{' '}
                    <code>COMMIT</code> и бэкап.
                  </div>
                </>
              ) : (
                <div className="font-semibold text-emerald-700">
                  Финансовые строки сохранены — потерь не обнаружено
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Таблицы по группам */}
      {GROUPS.map(group => (
        <Card key={group.title}>
          <div className="mb-1 font-semibold text-sm">{group.title}</div>
          <div className="mb-3 text-xs text-muted-foreground">{group.note}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b border-border">
                  <th className="text-left font-medium py-1.5 pr-2">Метрика</th>
                  <th className="text-right font-medium py-1.5 px-2 whitespace-nowrap">ДО</th>
                  <th className="text-right font-medium py-1.5 px-2 whitespace-nowrap">Сейчас</th>
                  <th className="text-right font-medium py-1.5 pl-2 whitespace-nowrap">Δ</th>
                </tr>
              </thead>
              <tbody>
                {group.metrics.map(m => {
                  const nowVal = now ? (now[m.key] as number) : null
                  const beforeVal = baseline ? (baseline[m.key] as number) : null
                  const delta =
                    nowVal != null && beforeVal != null ? round2(nowVal - beforeVal) : null
                  const breach = beforeVal != null && nowVal != null && isConservedBreach(m.kind, beforeVal, nowVal)
                  const orphanAbs = m.key === 'charges_orphaned' || m.key === 'payments_orphaned'
                  const absBad = orphanAbs && (nowVal ?? 0) > 0
                  const invariantGood = m.kind === 'invariant' && (nowVal ?? 1) === 0

                  return (
                    <tr key={m.key} className="border-b border-border/50 last:border-0">
                      <td className="py-1.5 pr-2">
                        <div className={breach || absBad ? 'text-red-600 font-medium' : ''}>
                          {m.label}
                        </div>
                        {m.hint && <div className="text-[11px] text-muted-foreground">{m.hint}</div>}
                      </td>
                      <td className="text-right py-1.5 px-2 tabular-nums text-muted-foreground whitespace-nowrap">
                        {fmt(m.kind, beforeVal)}
                      </td>
                      <td
                        className={
                          'text-right py-1.5 px-2 tabular-nums whitespace-nowrap font-medium ' +
                          (absBad
                            ? 'text-red-600'
                            : invariantGood
                              ? 'text-emerald-600'
                              : m.kind === 'invariant' && (nowVal ?? 0) > 0
                                ? 'text-amber-600'
                                : '')
                        }
                      >
                        {fmt(m.kind, nowVal)}
                      </td>
                      <td className="text-right py-1.5 pl-2 tabular-nums whitespace-nowrap">
                        {delta == null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : delta === 0 ? (
                          <span className="text-muted-foreground">0</span>
                        ) : (
                          <span
                            className={
                              breach
                                ? 'text-red-600 font-semibold'
                                : m.kind === 'invariant'
                                  ? 'text-emerald-600'
                                  : m.kind === 'contracts'
                                    ? 'text-amber-600'
                                    : 'text-foreground'
                            }
                          >
                            {delta > 0 ? '+' : ''}
                            {fmt(m.kind, delta)}
                            {breach && ' ⚠'}
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {/* Тяжёлый агрегат покрытия — по кнопке */}
      <Card>
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="font-semibold text-sm">🧮 Выручка без покрытия договором (R0)</div>
          <Button size="sm" variant="outline" onClick={() => void runCoverage()} disabled={coverageLoading}>
            <span className="inline-flex items-center gap-1.5">
              <RefreshCw size={14} className={coverageLoading ? 'animate-spin' : ''} />
              Посчитать
            </span>
          </Button>
        </div>
        <div className="mb-3 text-xs text-muted-foreground">
          Тяжёлый скан <code>studio_daily_totals</code> (только live-схема) — считается по кнопке.
          После разбора инверсий (R2) выручка под риском должна снижаться; сравнивайте значения
          между прогонами.
        </div>
        {coverage ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b border-border">
                  <th className="text-left font-medium py-1.5 pr-2">Метрика</th>
                  <th className="text-right font-medium py-1.5 pl-2">Сейчас</th>
                </tr>
              </thead>
              <tbody>
                {(
                  [
                    ['revenue_at_risk', 'Выручка под риском, ₽', true],
                    ['uncovered_days', 'Непокрытых дней', false],
                    ['days_without_ownership', '— из них без владения', false],
                    ['studios_affected', 'Затронуто студий', false],
                  ] as const
                ).map(([key, label, isMoney]) => (
                  <tr key={key} className="border-b border-border/50 last:border-0">
                    <td className="py-1.5 pr-2">{label}</td>
                    <td className="text-right py-1.5 pl-2 tabular-nums whitespace-nowrap font-medium">
                      {fmt(isMoney ? 'money' : 'conserved', coverage[key] as number)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2 text-[11px] text-muted-foreground">
              Окно: {coverage.window_from} … {coverage.window_to}. Посчитано: {fmtTime(coverage.captured_at)}
            </div>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">Ещё не считалось.</div>
        )}
      </Card>
    </div>
  )
}
